import React from "react";

import EndpointsAggregationFormula from "@common/assets/images/releases/de_aggregation_formula.png";
import EndpointsHealthFormula from "@common/assets/images/releases/endpoints_health_endpoint_formula.png";
import Button from "@common/components/Button/Button";
import Heading from "@common/components/Heading/Heading";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import useEndpointHealthScoring from "@main/pages/Release/consumers/useEndpointHealthScoring";
import { MetricScoreOutletContext } from "@main/pages/Release/views/MetricDetails/ReleaseMetricDetailsLayout";
import { MetricRow } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";
import * as MetricDetailsStyled from "main/pages/Release/views/MetricDetails/MetricDetails.styles";
import { useOutletContext } from "react-router-dom";

import * as Styled from "./EndpointHealthScoreView.styles";

export default function EndpointHealthScoreView() {
  const { refetchReleaseStatus } = useOutletContext<MetricScoreOutletContext>();
  const {
    factorA,
    setFactorA,
    factorB,
    setFactorB,
    factorC,
    setFactorC,
    weightD,
    setWeightD,
    weightE,
    setWeightE,
    weightError,
    scoringConfigLoading,
    handleSave,
    isSaving,
  } = useEndpointHealthScoring(refetchReleaseStatus);

  if (scoringConfigLoading) {
    return <Spinner />;
  }

  return (
    <div style={{ padding: "3.2rem 0" }}>
      <MetricDetailsStyled.MetricsCard>
        <Heading
          heading3
          marginBottom={24}
        >
          Score
        </Heading>
        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Scoring each endpoint
        </Heading>
        <Paragraph
          marginBottom={16}
          p1
        >
          First, each endpoint gets its own score:
        </Paragraph>
        <img
          style={{ marginBottom: "1.6rem" }}
          width={482}
          src={EndpointsHealthFormula}
        />
        <Styled.ThresholdList>
          <MetricRow
            label="a"
            value={factorA}
            step={0.1}
            min={0}
            onChange={setFactorA}
            description="This factor weights 3xx status codes (range: 0 to 1). A value of 0 applies maximum penalty, while 1 applies none. As 3xx responses are non-critical, we suggest a light penalty.   ←   System default: 0.7."
          />
          <MetricRow
            label="b"
            value={factorB}
            step={0.1}
            min={0}
            onChange={setFactorB}
            description="This factor weights 4xx status codes (0 to 1). 0 applies maximum penalty, while 1 applies none. As 4xx are client errors but impact UX, we suggest a moderate penalty.   ←   System default: 0.3."
          />
          <MetricRow
            label="c"
            value={factorC}
            onChange={setFactorC}
            description="This constant defines the severity of 5xx errors. The higher the value, the faster the score drops at the slightest occurrence of a 5xx response. Since 5xx errors are critical server failures, they are penalized heavily.   ←   System default: 50."
          />
        </Styled.ThresholdList>
        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Aggregation
        </Heading>
        <Paragraph marginBottom={24}>
          {"Then, all those scores are collected and the final 'Endpoints health' score is calculated:"}
        </Paragraph>

        <img
          style={{ marginBottom: "1.6rem" }}
          width={360}
          src={EndpointsAggregationFormula}
        />

        <Styled.ThresholdList>
          <MetricRow
            label="d"
            value={weightD}
            unit="%"
            onChange={setWeightD}
            description="The percentage weight of the worst-performing endpoint on the Endpoints health score. The goal is to penalize a single critical failure, as it can jeopardize the entire release.   ←   System default: 60%."
          />
          <MetricRow
            label="e"
            value={weightE}
            unit="%"
            isError={!!weightError}
            onChange={setWeightE}
            description="The percentage weight of the average score on the Endpoints health score. The sum of d + e must equal 100%.   ←   System default: 40%."
            error={weightError}
          />
        </Styled.ThresholdList>

        <Button
          type="submit"
          size="small"
          disabled={!!weightError || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </MetricDetailsStyled.MetricsCard>
    </div>
  );
}
